import { createReducer, on } from "@ngrx/store";
import { Attendance, AttendanceWhisper, Channel, CountState, Department, Integration, LeadSource, Message, Note, Pager, SalesMonitor, SettingsModule, Tabulation, Tag, User } from "lib-trend-core";
import { cloneDeep } from "lodash";
import * as AttendanceActions from "./actions";
import { AttendanceState, MessageHitsState } from "./app.state";

const initialMessageHits: MessageHitsState = {
  search: '',
  hits: [],
  messageOnlyCounts: [],
  page: 1,
  hasMore: false,
  windowMonths: 12,
  loading: false,
  error: false,
};

// initial global state
export const initialState: AttendanceState = {
  isLoading: false,
  company: undefined,
  user: undefined,
  pinnedAttendances: new Array<Attendance>(),
  attendance: undefined,
  attendances: new Pager<Attendance>(),
  notes: new Array<Note>(),
  tags: new Array<Tag>(),
  messages: new Pager<Message>(),
  copilotGateMessageCount: 0,
  error: null,
  pagerParams: undefined,
  integrations: new Array<Integration>(),
  settingsModules: new Array<SettingsModule>(),
  sidebarRight: false,
  contactInfo: false,
  counts: new Array<CountState>(),
  hasPipedriveIntegration: false,
  simpleTags: new Array<Tag>(),
  simpleDepartments: new Array<Department>(),
  simpleChannels: new Array<Channel>(),
  simpleUsers: new Array<User>(),
  simpleTabulations: new Array<Tabulation>(),
  simpleLeadSources: new Array<LeadSource>(),
  simpleSalesMonitors: new Array<SalesMonitor>(),
  messageHits: initialMessageHits,
  focusMessage: null,
  whispers: new Array<AttendanceWhisper>(),
  whisperPendingAttendanceIds: new Array<string>(),
}

//reducers
export const attendanceReducers = createReducer(
  initialState,
  on(AttendanceActions.clearFullState, (state) => ({ ...cloneDeep(initialState) })),

  on(AttendanceActions.getCounts, (state, action) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getPinnedAttendances, (state) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getAttendances, (state) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getCounts, (state, action) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getMessages, (state) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getNotes, (state) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getTags, (state) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getIntegrations, (state) => ({ ...state, isLoading: true })),
  on(AttendanceActions.getSettingsModules, (state) => ({ ...state, isLoading: true })),

  on(AttendanceActions.setCounts, (state, action) => ({ ...state, isLoading: false, counts: action.counts })),
  on(AttendanceActions.setAttendances, (state, action) => ({ ...state, isLoading: false, attendances: action.attendances })),
  on(AttendanceActions.setAttendance, (state, action) => ({ ...state, isLoading: false, attendance: action.attendance })),
  on(AttendanceActions.setTags, (state, action) => ({ ...state, isLoading: false, tags: action.tags })),
  on(AttendanceActions.setNotes, (state, action) => ({ ...state, isLoading: false, notes: action.notes })),
  on(AttendanceActions.setMessages, (state, action) => ({ ...state, isLoading: false, messages: action.messages })),
  on(AttendanceActions.setCopilotGateMessageCount, (state, action) => ({ ...state, copilotGateMessageCount: action.count })),
  on(AttendanceActions.bumpCopilotGateMessageCount, (state) => ({ ...state, copilotGateMessageCount: (state.copilotGateMessageCount ?? 0) + 1 })),
  on(AttendanceActions.setPagerParams, (state, action) => ({ ...state, isLoading: false, pagerParams: action.pagerParams })),
  on(AttendanceActions.setIntegrations, (state, action) => ({ ...state, isLoading: false, integrations: action.integrations, hasPipedriveIntegration: action.hasPipedriveIntegration })),
  on(AttendanceActions.setSimpleLists, (state, action) => ({ ...state, isLoading: false, simpleDepartments: action.departaments, simpleChannels: action.channels, simpleTags: action.tags, simpleUsers: action.users, simpleTabulations: action.tabulations, simpleLeadSources: action.leadSources, simpleSalesMonitors: action.salesMonitors })),
  on(AttendanceActions.setSettingsModules, (state, action) => ({ ...state, isLoading: false, settingsModules: action.settingsModules })),
  on(AttendanceActions.setPinnedAttendances, (state, action) => ({ ...state, isLoading: false, pinnedAttendances: action.pinnedAttendances })),

  // message search
  on(AttendanceActions.getMessageHits, (state, action) => ({
    ...state,
    messageHits: action.page === 1
      ? { ...initialMessageHits, search: action.search, params: action.params, loading: true }
      : { ...state.messageHits, loading: true },
  })),
  on(AttendanceActions.setMessageHits, (state, action) => ({
    ...state,
    messageHits: {
      ...state.messageHits,
      hits: action.result.meta.page > 1 ? [...state.messageHits.hits, ...action.result.data.hits] : action.result.data.hits,
      messageOnlyCounts: action.result.data.messageOnlyCountsByStatus,
      page: action.result.meta.page,
      hasMore: action.result.meta.hasMore,
      windowMonths: action.result.meta.windowMonths,
      loading: false,
      error: false,
    },
  })),
  on(AttendanceActions.messageHitsFailed, (state) => ({ ...state, messageHits: { ...state.messageHits, loading: false, error: true } })),
  on(AttendanceActions.clearMessageHits, (state) => ({ ...state, messageHits: initialMessageHits })),
  on(AttendanceActions.focusMessage, (state, action) => ({ ...state, focusMessage: action.focus })),
  on(AttendanceActions.clearFocusMessage, (state) => ({ ...state, focusMessage: null })),

  // whispers (never touch isLoading: a whisper request must not disturb the panel)
  on(AttendanceActions.setWhispers, (state, action) => ({ ...state, whispers: action.whispers })),
  on(AttendanceActions.setWhisperPending, (state, action) => ({ ...state, whisperPendingAttendanceIds: action.ids })),
  on(AttendanceActions.addWhisperPending, (state, action) => ({
    ...state,
    whisperPendingAttendanceIds: state.whisperPendingAttendanceIds.includes(action.idAttendance)
      ? state.whisperPendingAttendanceIds
      : [...state.whisperPendingAttendanceIds, action.idAttendance],
  })),
  on(AttendanceActions.removeWhisperPending, (state, action) => ({
    ...state,
    whisperPendingAttendanceIds: state.whisperPendingAttendanceIds.filter((id) => id !== action.idAttendance),
  })),

  on(AttendanceActions.adjustUnreadCount, (state, action) => ({
    ...state,
    counts: state.counts.map((row) => row.status === action.status
      ? { ...row, unread: Math.max(0, (row.unread ?? 0) + action.delta) }
      : row),
  })),

  on(AttendanceActions.openCloseSidebarRight, (state, action) => ({ ...state, isLoading: false, sidebarRight: action.sidebarRight })),
  on(AttendanceActions.openCloseContactInfo, (state, action) => ({ ...state, isLoading: false, contactInfo: action.contactInfo })),

  // init
  on(AttendanceActions.setLoadInit, (state, action) => ({ ...state, user: action.user, company: action.company })),

  // failure
  on(AttendanceActions.setFailure, (state, action) => ({ ...state, error: action.error })),
);
